import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Edit, Printer } from 'lucide-react';
import MagasinBadge from '../../components/MagasinBadge';
import { repairStatusBadge } from './reparationsUtils';
import { useLanguage } from '../../contexts/LanguageContext';

export const RepSection = ({ title, children, testId, className = '' }) => (
  <section
    className={`rounded-xl border border-white/10 bg-[#151820] p-4 sm:p-5 ${className}`}
    data-testid={testId}
  >
    {title && (
      <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">{title}</h3>
    )}
    {children}
  </section>
);

export const RepField = ({ label, value, children, testId, className = '' }) => (
  <div className={className} data-testid={testId}>
    <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">{label}</div>
    {children || <div className="text-sm text-white font-medium">{value || '—'}</div>}
  </div>
);

export const RepFormSection = ({ title, children }) => (
  <div className="space-y-4 pt-2 border-t border-white/10 first:border-t-0 first:pt-0">
    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">{title}</h3>
    {children}
  </div>
);

export const RepFicheHeader = ({
  refCode,
  device,
  onPrint,
  backTo = '/reparations',
  backLabel,
}) => {
  const { t } = useLanguage();
  const tone = repairStatusBadge(device?.status);
  const resolvedBackLabel = backLabel || t('repairUi.repairs');
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4" data-testid="repair-detail-header">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 min-w-0">
        <Link
          to={backTo}
          className="text-sm text-gray-400 hover:text-white flex items-center gap-1.5 shrink-0"
        >
          <ArrowLeft size={16} /> {resolvedBackLabel}
        </Link>
        <h1 className="text-lg font-bold text-white">
          {t('repairUi.repairSheet')}{' '}
          <span className="text-red-400 font-mono">{refCode}</span>
        </h1>
        {device?.magasin_id && <MagasinBadge code={device.magasin_id} size="md" />}
        {device?.status && (
          <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${tone.className}`} data-testid="repair-status">
            {tone.label}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-2 shrink-0">
        <Link
          to={`/reparations/modifier/${refCode}`}
          className="min-h-[40px] px-4 rounded-lg bg-white/10 hover:bg-white/15 text-sm font-semibold text-gray-200 flex items-center gap-2"
          data-testid="edit-repair-btn"
        >
          <Edit size={16} /> {t('repairUi.edit')}
        </Link>
        <button
          type="button"
          onClick={onPrint}
          className="min-h-[40px] px-4 rounded-lg bg-red-600 hover:bg-red-500 text-sm font-semibold text-white flex items-center gap-2"
          data-testid="print-repair-btn"
        >
          <Printer size={16} /> {t('repairUi.print')}
        </button>
      </div>
    </div>
  );
};

export const RepFormHeader = ({ isEditing, refCode, magasinId, onBack }) => {
  const { t } = useLanguage();
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
      <div>
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-3 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('repairUi.backToRepairs')}
        </button>
        <h2 className="text-xl font-bold text-white">
          {isEditing ? t('repairUi.editRepair') : t('repairUi.newDeposit')}
        </h2>
        {isEditing && refCode && (
          <p className="text-sm text-gray-500 mt-1">
            {t('repairUi.ref')} · <span className="text-red-400 font-mono font-semibold">{refCode}</span>
          </p>
        )}
      </div>
      {magasinId && (
        <MagasinBadge code={magasinId} size="md" />
      )}
    </div>
  );
};
